import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CalendarClock, AlertTriangle, ListChecks, MapPin } from "lucide-react";
import { fmtTime, fmtDate } from "@/lib/format";

/**
 * Today strip — the "what matters right now" summary at the top of Home.
 * Cast see next call. Staff/Admin see today's rehearsals + unread signals.
 * Fetches lightweight aggregate from existing endpoints; renders in ≤80 lines.
 */
export default function HomeTodayStrip({ role }) {
  const [rehearsals, setRehearsals] = useState([]);
  const [conflicts, setConflicts] = useState(0);
  const [openActionables, setOpenActionables] = useState(0);
  const [myCalls, setMyCalls] = useState([]);
  const isCast = role === "cast";

  useEffect(() => {
    api.get("/rehearsals").then((r) => setRehearsals(r.data)).catch(() => {});
    if (!isCast) {
      api.get("/conflicts").then((r) => setConflicts((r.data || []).filter((c) => c.status === "pending").length)).catch(() => {});
      api.get("/actionables", { params: { status: "open" } }).then((r) => setOpenActionables((r.data || []).length)).catch(() => {});
    } else {
      api.get("/my-calls").then((r) => setMyCalls(r.data || [])).catch(() => {});
    }
  }, [isCast]);

  const todayISO = new Date().toISOString().slice(0, 10);
  const todaysRehearsals = useMemo(() => rehearsals.filter((r) => r.date === todayISO), [rehearsals, todayISO]);
  const nextRehearsal = useMemo(() => {
    const upcoming = rehearsals
      .filter((r) => r.date >= todayISO)
      .sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time));
    return upcoming[0];
  }, [rehearsals, todayISO]);

  const nextMyCall = useMemo(() => {
    const upcoming = myCalls
      .filter((c) => c.date >= todayISO)
      .sort((a, b) => (a.date + (a.call_time || "")).localeCompare(b.date + (b.call_time || "")));
    return upcoming[0];
  }, [myCalls, todayISO]);

  if (isCast) {
    return (
      <div className="rounded-2xl border border-gold/30 bg-gradient-to-r from-gold/10 via-transparent to-transparent p-5 md:p-6" data-testid="today-strip-cast">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Your next call</div>
            {nextMyCall ? (
              <>
                <div className="mt-1 font-display text-2xl md:text-3xl leading-tight">
                  {nextMyCall.date === todayISO ? "Today" : fmtDate(nextMyCall.date)}
                  <span className="text-gold ml-3">{fmtTime(nextMyCall.call_time)}</span>
                </div>
                <div className="mt-1 text-sm text-zinc-400 inline-flex items-center gap-2">
                  {nextMyCall.rehearsal_title || "Rehearsal"}
                  {nextMyCall.location && <><span className="text-zinc-600">·</span><MapPin className="w-3 h-3" />{nextMyCall.location}</>}
                </div>
              </>
            ) : (
              <div className="mt-1 text-sm text-zinc-400">No upcoming calls scheduled. Enjoy the break.</div>
            )}
          </div>
          <Link to="/calendar" className="text-xs uppercase tracking-widest text-gold hover:brightness-110" data-testid="today-strip-cast-open-calendar">Full calendar →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-5" data-testid="today-strip-staff">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/calendar" className="group flex items-start gap-3 hover:bg-white/[0.02] rounded-lg p-2 -m-2 transition-colors" data-testid="today-strip-rehearsals">
          <div className="w-9 h-9 rounded-md grid place-items-center bg-gold/10 text-gold shrink-0"><CalendarClock className="w-4 h-4" /></div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Today</div>
            <div className="text-xl font-display leading-tight">
              {todaysRehearsals.length === 0 ? "No rehearsals" : `${todaysRehearsals.length} rehearsal${todaysRehearsals.length === 1 ? "" : "s"}`}
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              {nextRehearsal
                ? `Next: ${nextRehearsal.date === todayISO ? "today" : fmtDate(nextRehearsal.date)} · ${fmtTime(nextRehearsal.start_time)}`
                : "Nothing on the books"}
            </div>
          </div>
        </Link>
        <Link to="/conflicts" className="group flex items-start gap-3 hover:bg-white/[0.02] rounded-lg p-2 -m-2 transition-colors" data-testid="today-strip-conflicts">
          <div className={`w-9 h-9 rounded-md grid place-items-center shrink-0 ${conflicts > 0 ? "bg-red-500/10 text-red-400" : "bg-zinc-500/10 text-zinc-500"}`}><AlertTriangle className="w-4 h-4" /></div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Conflicts</div>
            <div className="text-xl font-display leading-tight">{conflicts} unreviewed</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">{conflicts > 0 ? "Cast asked for time off" : "All clear"}</div>
          </div>
        </Link>
        <Link to="/rehearsals?tab=actionables" className="group flex items-start gap-3 hover:bg-white/[0.02] rounded-lg p-2 -m-2 transition-colors" data-testid="today-strip-actionables">
          <div className="w-9 h-9 rounded-md grid place-items-center bg-emerald-500/10 text-emerald-300 shrink-0"><ListChecks className="w-4 h-4" /></div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Open actionables</div>
            <div className="text-xl font-display leading-tight">{openActionables}</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">Team follow-ups in flight</div>
          </div>
        </Link>
      </div>
    </div>
  );
}
